import './style.css';
import moment from "moment";
import ButtonPrimary from "../buttons/primary/primary";
import Loader from "../loader/loader";  
import FormField from "./form-field";
import SecondaryButton from "../buttons/secondary/secondary";
import RubricaModal from "../modals/rubrica/rubrica-modal";
import RubricasSection from "./components/form-rubrica-section";
import storage from "../../shared/services/storage";
import { useForm } from "react-hook-form";
import { useCallback, useEffect, useId, useState, useMemo } from "react";
import { getMyAccessService } from  "../../features/user/service/user-service";
import { User } from "../../shared/interfaces/user";
import { SEVERITY } from "../../shared/interfaces/alert";
import { useAlert } from "../alert/alert";
import { FormProps, FormTypes } from "../../shared/interfaces/form";
import MenuContext, { DISPLAY_TYPE } from '../menu/context/menu-context';
import { FormComponentProps } from './interface/form';
import { useFieldRulesProcessor, createMonetaryFieldsRules } from '../../shared/hooks/useFieldRule';
import { ProjectRubrica } from '../../shared/interfaces/rubrica';

interface FormSection {
  title: string;
  icon: string;
  color: string;
  fields: FormProps[];
}

interface FormComponentPropsWithSections extends FormComponentProps {
  sections?: FormSection[];
  onRubricasUpdate?: (rubricas: ProjectRubrica[]) => void;
}

export default function Form({ 
  data, 
  sections,
  title, 
  onSubmitForm, 
  onCancel, 
  isLoading = false, 
  isReadOnly = false,
  onClickedMenuContextType,
  showEdit = false,
  showFinancial = false,
  showModalFinancial = false,
  showDeleteIcon = false,
  showFinished = false,
  onFieldValueChange,
  onChangeCheckbox,
  onRubricasUpdate,
  fieldRules = []
}: FormComponentPropsWithSections) {
  const baseId = useId();
  const { showAlert } = useAlert();
  const { 
    handleSubmit, 
    formState: { errors, isValid }, 
    reset, 
    control, 
    setValue, 
    getValues,
    watch 
  } = useForm({ shouldUnregister: false });
  
  const [ user, setUser ] = useState<User>();
  const [ isEditor, setIsEditor ] = useState<boolean>(false);
  const [ isLocalLoading, setIsLocalLoading ] = useState<boolean>(false);
  const [ formFields, setFormFields ] = useState<FormProps[]>(data || []);
  
  const [ isRubricaModalOpen, setIsRubricaModalOpen ] = useState<boolean>(false);
  const [ projectRubricas, setProjectRubricas ] = useState<ProjectRubrica[]>([]);
  const [ rubricasLoaded, setRubricasLoaded ] = useState<boolean>(false);

  const shouldRenderSections = sections && sections.length > 0;

  const syncProjectValue = useCallback((rubricas: ProjectRubrica[]) => {
    const totalValue = calculateTotalFromRubricas(rubricas);
    setValue('valor', totalValue / 100);
    if (onFieldValueChange) {
      onFieldValueChange('valor', totalValue / 100);
    }
  }, [setValue, onFieldValueChange]);

  const defaultMonetaryRules = useMemo(() => createMonetaryFieldsRules(
    'valor', 
    ['permanentMaterialValor', 'materialConsumptionValor', 'personalValor']
  ), []);

  const allRules = useMemo(() => [...defaultMonetaryRules, ...fieldRules], 
    [defaultMonetaryRules, fieldRules]);

  const { shouldFieldBeDisabled } = useFieldRulesProcessor(allRules);
  const isFinalLoading = isLoading || isLocalLoading;

  const isProjectForm = useMemo(() => {
    const hasProjectFields = formFields.some(field => 
      ['principalInvestigatorId', 'financierId', 'duration', 'initialDate', 'finalDate'].includes(field.name)
    );
    
    const isProjectTitle = title.toLowerCase().includes('projeto') && 
                          !title.toLowerCase().includes('financeira') && 
                          !title.toLowerCase().includes('movimentação') && 
                          !title.toLowerCase().includes('beneficiário');
    
    return hasProjectFields || isProjectTitle;
  }, [formFields, title]);
  
  const isAddMode = title.includes('Adicionar');
  const isEditMode = title.includes('Editar');
  const shouldShowRubricaSection = (isAddMode || isEditMode) && isProjectForm;
  
  const updateFieldsDisabledStatus = useCallback(() => {
    const currentValues = getValues();
    setFormFields(prevFields => {
      if (!prevFields || !prevFields.length) return prevFields;
      return prevFields.map(field => {
        const shouldBeDisabled = shouldFieldBeDisabled(field.name, currentValues);
        if (Boolean(field.disabled) !== shouldBeDisabled) {
          return { ...field, disabled: shouldBeDisabled };
        }
        return field;
      });
    });
  }, [getValues, shouldFieldBeDisabled]);
  
  const calculateEndDate = useCallback(() => {
    const initialDateValue = getValues("initialDate");
    const duration = getValues("duration");
    if (!initialDateValue || !duration || isNaN(parseInt(duration))) return;
    try {
      const startDate = moment(initialDateValue);
      if (!startDate.isValid()) return;
      const endDate = startDate.clone().add(parseInt(duration), 'months');
      setValue('finalDate', endDate.toDate());
    } catch (error) {
      console.error('Erro ao calcular data final:', error);
    }
  }, [getValues, setValue]);

  const calculateDurationInMonths = useCallback(() => {
    const initialDateValue = getValues("initialDate");
    const finalDateValue = getValues("finalDate");
    if (!initialDateValue || !finalDateValue) return;
    try {
      const startDate = moment(initialDateValue);
      const endDate = moment(finalDateValue);
      if (!startDate.isValid() || !endDate.isValid()) return;
      const duration = endDate.diff(startDate, 'months');
      setValue('duration', duration);
    } catch (error) {
      console.error('Erro ao calcular duração em meses:', error);
    }
  }, [getValues, setValue]);

  const calculateTotalFromRubricas = useCallback((rubricas: ProjectRubrica[]) => {
    return rubricas.reduce((total, rubrica) => total + Number(rubrica.valor || 0), 0);
  }, []);

  const loadExistingRubricas = useCallback((defaultValues: Record<string, any>) => {
    if (rubricasLoaded || !shouldShowRubricaSection) return;

    if (isEditMode && defaultValues.rubricasWithValues) {
      try {
        const rubricasData = Array.isArray(defaultValues.rubricasWithValues)
          ? defaultValues.rubricasWithValues
          : [];

        const rubricas = rubricasData.map((r: any) => {
          const id = (r.id || r.rubricaId || '').toString();
          const name = r.name || r.rubrica?.name || '';
          const description = r.description || r.rubrica?.description || '';
          const valor = Number(r.valor || 0);

          return {
            rubricaId: id,
            valor,
            name,
            description,
            rubrica: { id, name, description }
          };
        });

        setProjectRubricas(rubricas);
        syncProjectValue(rubricas);
        setRubricasLoaded(true);
      } catch (error) {
        console.error('Erro ao carregar rubricas existentes:', error);
        showAlert('Erro ao carregar rubricas do projeto', SEVERITY.ERROR);
      }
    } else {
      setProjectRubricas([]);
      setRubricasLoaded(true);
    }
  }, [isEditMode, showAlert, rubricasLoaded, shouldShowRubricaSection, syncProjectValue]);

  const handleOpenRubricaModal = () => setIsRubricaModalOpen(true);
  const handleCloseRubricaModal = () => setIsRubricaModalOpen(false);

  // ✅ Função de submit que pode usar rubricas passadas ou do estado
  const submitFormData = useCallback((rubricasToUse?: ProjectRubrica[]) => {
    const formData = { ...getValues() };
    
    try {
      // Processar datas (se existirem)
      if (formData?.initialDate) {
        formData.initialDate = moment(formData.initialDate).toISOString();
      }
      if (formData?.finalDate) {
        formData.finalDate = moment(formData.finalDate).toISOString();
      }

      // ✅ Limpar campos que não devem ser enviados
      delete formData.rubricasWithValues;
      delete formData.permanentMaterialValor;
      delete formData.materialConsumptionValor;
      delete formData.personalValor;

      // ✅ Corrigir multiplePaymentsRecurrency
      if (formData.multiplePaymentsRecurrency === '' || formData.multiplePaymentsRecurrency === undefined) {
        formData.multiplePaymentsRecurrency = null;
      }

      // Usar rubricas passadas ou do estado
      const rubricasFinais = rubricasToUse ?? projectRubricas;

      // Processar rubricas
      if (rubricasFinais.length > 0 && shouldShowRubricaSection) {
        const totalValue = calculateTotalFromRubricas(rubricasFinais);
        
        // ✅ CORRIGIDO: Multiplicar por 100 para converter para centavos
        formData.valor = Math.round(totalValue * 100);

        formData.rubricas = rubricasFinais.map((rubrica) => ({
          rubricaId: rubrica.rubricaId,
          valor: Number(rubrica.valor),
          name: rubrica.rubrica?.name ?? rubrica.name ?? null,
          description: rubrica.rubrica?.description ?? rubrica.description ?? null,
        }));
      } else if (shouldShowRubricaSection) {
        // Se não tem rubricas mas é formulário de projeto, enviar array vazio
        formData.valor = 0;
        formData.rubricas = [];
      } else if (!shouldShowRubricaSection) {
        formData.valor = Math.round(Number(formData.valor) * 100);
      }

      // Adicionar companyId se necessário
      if (!isEditor && user?.companyId) {
        formData.companyId = user.companyId;
      }

      onSubmitForm?.(formData);
    } catch (error) {
      showAlert("Erro ao processar formulário", SEVERITY.ERROR);
    }
  }, [getValues, projectRubricas, shouldShowRubricaSection, calculateTotalFromRubricas, isEditor, user, onSubmitForm, showAlert]);

  // ✅ MODIFICADO: handleSaveRubricas agora faz auto-save
  const handleSaveRubricas = useCallback((rubricas: ProjectRubrica[]) => {
    const normalized = (rubricas || [])
      .filter(r => r.rubricaId && Number(r.valor) > 0)
      .map(r => {
        const id = String(r.rubricaId);
        const name = r.name || r.rubrica?.name || '';
        const description = r.description || r.rubrica?.description || '';
        const valor = Number(r.valor || 0);
        return {
          rubricaId: id,
          valor,
          name,
          description,
          rubrica: { id, name, description }
        };
      });

    // Atualizar estado local
    setProjectRubricas(normalized);
    syncProjectValue(normalized);
    onRubricasUpdate?.(normalized);

    // ✅ AUTO-SAVE: Salvar automaticamente após fechar o modal (apenas no modo edição)
    if (isEditMode) {
      // Usar setTimeout para garantir que o estado foi atualizado
      setTimeout(() => {
        submitFormData(normalized);
      }, 100);
    } else {
      // No modo adicionar, apenas mostrar mensagem de que foi atualizado localmente
      showAlert('Rubricas atualizadas. Clique em Salvar para criar o projeto.', SEVERITY.INFO);
    }
  }, [syncProjectValue, onRubricasUpdate, isEditMode, submitFormData, showAlert]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  
  const handleDurationBlur = () => calculateEndDate();
  const handleDateChange = () => calculateDurationInMonths();

  const fetchMyacess = useCallback(async () => {
    const url = window.location.pathname;
    try {
      setIsLocalLoading(true);
      const userLogged = await storage.getStorage('user');
      const userData = JSON.parse(userLogged || '');
      const myAcess = await getMyAccessService(userData.id);
      myAcess?.role.verboseAcess.find((verbose: string) => 
        (verbose === 'PUT' || verbose === 'POST') && !url.includes('list')
      ) ? setIsEditor(true) : setIsEditor(false);
      setUser(myAcess);
    } catch (error) {
      showAlert('Erro ao carregar suas permissões', SEVERITY.ERROR);
    } finally {
      setIsLocalLoading(false);
    }
  }, [showAlert]);

  // ✅ onSubmit normal para o botão Salvar do formulário
  const onSubmit = (data: any) => {
    try {
      const formData = new FormData();

      if (!data?.accountabilitty) formData.delete("accountabilitty");

      Object.keys(data).forEach((key) => {
        if (key === "recipt" && data[key] instanceof File) {
          formData.append("recipt", data[key]);
        } else if (data[key] !== null && data[key] !== undefined) {
          if (typeof data[key] === "object" && !(data[key] instanceof File)) {
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        }
      });

      // Processar datas (se existirem)
      if (data?.initialDate) {
        data.initialDate = moment(data.initialDate).toISOString();
      }
      if (data?.finalDate) {
        data.finalDate = moment(data.finalDate).toISOString();
      }

      // ✅ Limpar campos que não devem ser enviados
      delete data.rubricasWithValues;
      delete data.permanentMaterialValor;
      delete data.materialConsumptionValor;
      delete data.personalValor;

      // ✅ Corrigir multiplePaymentsRecurrency
      if (data.multiplePaymentsRecurrency === '' || data.multiplePaymentsRecurrency === undefined) {
        data.multiplePaymentsRecurrency = null;
      }

      // Processar rubricas SEMPRE (independente das datas)
      if (projectRubricas.length > 0 && shouldShowRubricaSection) {
        const totalValue = calculateTotalFromRubricas(projectRubricas);
        
        // ✅ CORRIGIDO: Multiplicar por 100 para converter para centavos
        data.valor = Math.round(totalValue * 100);

        data.rubricas = projectRubricas.map((rubrica) => ({
          rubricaId: rubrica.rubricaId,
          valor: Number(rubrica.valor),
          name: rubrica.rubrica?.name ?? rubrica.name ?? null,
          description: rubrica.rubrica?.description ?? rubrica.description ?? null,
        }));
      } else if (shouldShowRubricaSection) {
        // Se não tem rubricas mas é formulário de projeto
        data.valor = 0;
        data.rubricas = [];
      } else if (!shouldShowRubricaSection) {
        // Só multiplica por 100 se NÃO for seção de rubricas
        data.valor = Math.round(Number(data.valor) * 100);
      }

      // Adicionar companyId se necessário
      if (!isEditor && user?.companyId) {
        data.companyId = user.companyId;
      }

      onSubmitForm?.(data);
    } catch (error) {
      showAlert("Erro ao processar formulário", SEVERITY.ERROR);
    }
  };

  const handleCancel = () => onCancel?.();
  const handleCheckboxChange = useCallback((value: boolean, fieldName: string) => {
    onChangeCheckbox?.(value, fieldName);
  }, [onChangeCheckbox]);

  const notifyParentAboutFieldChange = useCallback((fieldName: string, value: any) => {
    if (onFieldValueChange) {
      setTimeout(() => onFieldValueChange(fieldName, value), 0);
    }
  }, [onFieldValueChange]);

  const renderSectionFields = (fields: FormProps[], sectionIndex: number) => (
    fields.map((item, index) => (
      <FormField
        key={`section-${sectionIndex}-field-${index}-${baseId}`}
        item={item}
        index={index}
        baseId={baseId}
        errors={errors}
        control={control}
        isEditor={isEditor}
        isFinalLoading={isFinalLoading}
        handleDurationBlur={handleDurationBlur}
        handleDateChange={handleDateChange}
        handleCheckboxChange={handleCheckboxChange}
        setValue={setValue}
        getValues={getValues}
      />
    ))
  );

  const renderNormalFields = () => (
    formFields?.map((item, index) => (
      <FormField
        key={`form-field-${index}-${baseId}`}
        item={item}
        index={index}
        baseId={baseId}
        errors={errors}
        control={control}
        isEditor={isEditor}
        isFinalLoading={isFinalLoading}
        handleDurationBlur={handleDurationBlur}
        handleDateChange={handleDateChange}
        handleCheckboxChange={handleCheckboxChange}
        setValue={setValue}
        getValues={getValues}
      />
    ))
  );

  useEffect(() => {
    const monetaryFields = ['valor', 'permanentMaterialValor', 'materialConsumptionValor', 'personalValor'];
    const subscription = watch((values, { name }) => {
      if (name && monetaryFields.includes(name)) {
        updateFieldsDisabledStatus();
        notifyParentAboutFieldChange(name, values[name]);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, updateFieldsDisabledStatus, notifyParentAboutFieldChange]);

  useEffect(() => {
    if (data && data.length > 0) {
      setFormFields([...data]);
      setTimeout(() => {
        if(fieldRules.length > 0) updateFieldsDisabledStatus();
      }, 100);
    }
  }, [data, updateFieldsDisabledStatus]);

  useEffect(() => {
    if (!data || data.length === 0) return;
    const dataString = JSON.stringify(data);
    const currentDataString = JSON.stringify(formFields);
    if (dataString === currentDataString && rubricasLoaded) return;
    const defaultValues = data.reduce((acc, item) => {
      acc[item.name] = item.typeOfField === FormTypes.DATE ? item.value || null : item.value || "";
      return acc;
    }, {} as Record<string, any>);
    reset(defaultValues);
    fetchMyacess();
    if (shouldShowRubricaSection) loadExistingRubricas(defaultValues);
    setTimeout(() => {
      if (title.includes('Editar')) calculateEndDate();
    }, 300);
  }, [data, shouldShowRubricaSection]);

  return (
    <>
      <form className="container-form">
        {isLoading && (
          <div className="form-loader-overlay">
            <div className="form-loader-container">
              <Loader />
            </div>
          </div>
        )}
        <div className="container-form__container">
          <div className="container-form__header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="container-form__title">{title}</h2>
              <MenuContext 
                display={DISPLAY_TYPE.INLINE}
                showAdd={false}
                showEdit={showEdit}
                clickedEdit={onClickedMenuContextType ? () => onClickedMenuContextType('edit') : undefined}
                showFinancial={showFinancial}
                showFinancialModal={showModalFinancial}
                showDeleteIcon={showDeleteIcon}
                showFinished={showFinished}
                clickedFinancial={onClickedMenuContextType ? () => onClickedMenuContextType('financial') : undefined}
                clickedFinancialModal={onClickedMenuContextType ? () => onClickedMenuContextType('financialModal') : undefined}
                clickedDelete={onClickedMenuContextType ? () => onClickedMenuContextType('delete') : undefined}
                clickedFinished={onClickedMenuContextType ? () => onClickedMenuContextType('finished') : undefined}
              />
            </div>
          </div>

          {shouldRenderSections ? (
            <div className="container-form__sections">
              {sections.map((section, sectionIndex) => (
                <div key={`section-${sectionIndex}`} className={`form-section form-section--${section.color}`}>
                  <div className="form-section__header">
                    <div className="form-section__title-container">
                      <h3 className="form-section__title">
                        {section.title}
                      </h3>
                    </div>
                  </div>
                  <div className="form-section__content">
                    <div className="container-form__fields">
                      {renderSectionFields(section.fields, sectionIndex)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="container-form__fields">
              {renderNormalFields()}
            </div>
          )}

          {shouldShowRubricaSection && (
            <RubricasSection
              projectRubricas={projectRubricas}
              onOpenModal={handleOpenRubricaModal}
              isEditor={isEditor}
              isLoading={isFinalLoading}
              formatCurrency={formatCurrency}
              calculateTotalFromRubricas={calculateTotalFromRubricas}
            />
          )}

          <div className="container-form__actions">
            <SecondaryButton  
              title="Cancelar"  
              onClick={handleCancel}
            />
            {user?.role?.verboseAcess?.includes('PUT') && !isReadOnly && (
              <ButtonPrimary  
                title={title.includes('Editar') ? 'Salvar' : 'Salvar'} 
                onClick={handleSubmit((data)=> onSubmit(data))}
                disabled={!isEditor || isFinalLoading || !isValid}
              />
            )}
          </div>
        </div>
      </form>

      {shouldShowRubricaSection && isRubricaModalOpen && (
        <RubricaModal
          isOpen={isRubricaModalOpen}
          onClose={handleCloseRubricaModal}
          onSave={handleSaveRubricas}
          initialRubricas={[...projectRubricas]}
          isLoading={isFinalLoading}
        />
      )}
    </>
  );
}